/**
 * One technology's book, opened out into the demands behind it.
 *
 * THE BREAKDOWN COMES FROM THE ROLLUP, in `project_rows` on the row itself. Do
 * not re-fetch it from /api/projects, which is wrong twice over: that endpoint
 * returns project-MONTHS rather than projects and caps at 200 rows, so Retool's
 * 390 project-months arrive truncated and its 104 projects collapse to 73 -- a
 * drill-in quietly disagreeing with the row that opened it. The figures here
 * are computed where the total is computed, which is the only construction that
 * can be relied on to agree with it.
 *
 * Account and contract type read the way they do on the risk board, for the
 * same reasons: the SOW number is paperwork and the account is the
 * relationship, and a fixed-price overrun means something different from a
 * T&M one.
 */
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { DrillInDialog } from "@/components/profitability/DrillInDialog";
import { MarginCell } from "@/components/profitability/MarginCell";
import { BillingTypeCell } from "@/components/profitability/RiskBasisBadge";
import { DataTable } from "@/components/shared/data-table";
import { useClientSort } from "@/hooks/use-client-sort";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours as formatHours, money } from "@/lib/format";
import type { TechnologyProjectRow, TechnologyRow } from "@/lib/profitability-api";

const EMPTY_PROJECTS: TechnologyProjectRow[] = [];

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
    </div>
  );
}

export function TechnologyDrillInDialog({
  row,
  range,
  basis,
  onOpenChange,
}: {
  row: TechnologyRow | null;
  range?: string;
  basis?: string;
  onOpenChange: (open: boolean) => void;
}) {
  const projects = row?.project_rows ?? EMPTY_PROJECTS;
  // The whole set is already here, so sorting is a local reorder rather than a
  // round trip -- the same arrangement the page behind this dialog uses.
  const sorted = useClientSort(
    projects,
    (project, key) => project[key as keyof TechnologyProjectRow] as string | number | null,
    { key: "revenue", dir: "desc" },
  );

  return (
    <DrillInDialog
      open={row !== null}
      onOpenChange={onOpenChange}
      title={row ? row.technology : ""}
      description={
        row
          ? `${row.projects} project${row.projects === 1 ? "" : "s"} · ${money(row.revenue)} revenue`
          : undefined
      }
      action={
        <Link
          to="/dashboard/projects"
          search={{ range }}
          onClick={() => onOpenChange(false)}
          className="inline-flex items-center gap-1 text-sm font-medium hover:underline"
        >
          All projects
          <ExternalLink className="size-3.5" aria-hidden />
        </Link>
      }
    >
      {row ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <Figure label="Revenue" value={money(row.revenue)} />
            <Figure label="Cost" value={money(row.cost)} />
            <Figure label="Profit" value={money(row.profit)} note={`${row.margin_pct ?? "—"}%`} />
            <Figure label="Hours" value={formatHours(row.hours)} />
            <Figure label="Projects" value={String(row.projects)} />
          </div>

          {row.is_untagged ? (
            <p className="rounded-md border bg-muted/30 p-3 text-sm">
              This is not a technology. It is every demand carrying no platform tag, grouped so the
              board's total still matches the company's rather than quietly shrinking.
            </p>
          ) : null}

          {/* The row's own count against what arrived. They agree today; if they
              ever disagree the dialog says so rather than showing a shorter
              list under a number that claims otherwise. */}
          {projects.length !== row.projects ? (
            <p className="text-xs text-muted-foreground">
              Showing {projects.length} of the {row.projects} projects counted in this row.
            </p>
          ) : null}

          <DataTable
            sort={sorted.sort}
            sortDir={sorted.sortDir}
            onSort={sorted.toggleSort}
            headers={[
              { label: "Project", sortKey: "project_name", help: COLUMN_HELP.project },
              { label: "Contract", sortKey: "billing_type", help: COLUMN_HELP.riskContract },
              {
                label: "Revenue",
                sortKey: "revenue",
                className: "text-right",
                help: COLUMN_HELP.revenue,
              },
              { label: "Cost", sortKey: "cost", className: "text-right", help: COLUMN_HELP.cost },
              {
                label: "Margin",
                sortKey: "margin_pct",
                className: "text-right",
                help: COLUMN_HELP.margin,
              },
              {
                label: "Hours",
                sortKey: "hours",
                className: "text-right",
                help: COLUMN_HELP.hours,
              },
              {
                label: "Months",
                sortKey: "months",
                className: "text-right",
                help: COLUMN_HELP.technologyMonths,
              },
            ]}
          >
            {sorted.rows.map((project) => (
              <tr
                key={project.demand_id}
                className="border-b border-border last:border-0 hover:bg-muted/30"
              >
                <td className="px-4 py-3 font-medium">
                  <Link
                    to="/dashboard/projects/$demandId"
                    params={{ demandId: project.demand_id }}
                    search={{ range }}
                    onClick={() => onOpenChange(false)}
                    className="hover:underline"
                  >
                    {project.project_name ?? project.demand_id}
                  </Link>
                  {project.account_name ? (
                    <span className="block text-xs font-normal text-muted-foreground">
                      {project.account_name}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3">
                  <BillingTypeCell billingType={project.billing_type} />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                  {money(project.revenue)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                  {money(project.cost)}
                </td>
                <td className="px-4 py-3 text-right">
                  <MarginCell value={project.margin_pct} />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums">
                  {formatHours(project.hours)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{project.months}</td>
              </tr>
            ))}
          </DataTable>

          {basis ? <p className="text-xs text-muted-foreground">{basis}</p> : null}
        </div>
      ) : null}
    </DrillInDialog>
  );
}
